// src/loaders/authLoader.ts
// React Router v7 loader that guards protected routes.
// Called before rendering any page inside RootLayout.
// Redirects to /auth/login if there is no active session.

import { redirect } from 'react-router-dom'
import { supabase } from '@/lib/supabase'

/**
 * Auth loader — protects all routes inside the (root) layout.
 * React Router v7 calls this before rendering the component.
 */
export async function authLoader() {
  const { data: { session }, error } = await supabase.auth.getSession()

  if (error) {
    console.error('authLoader: session error', error)
  }

  // No session → redirect to login
  if (!session) {
    throw redirect('/auth/login')
  }

  // Optionally check that the user has admin/pastor role
  const { data: profile } = await supabase
    .from('profiles')
    .select('role, is_active')
    .eq('id', session.user.id)
    .single()

  // Inactive account → sign out and redirect
  if (profile && !profile.is_active) {
    await supabase.auth.signOut()
    throw redirect('/auth/login?error=account_inactive')
  }

  // Member-only accounts cannot access the admin dashboard
  if (profile && profile.role === 'member') {
    await supabase.auth.signOut()
    throw redirect('/auth/login?error=insufficient_permissions')
  }

  // All good — return session so the layout can access it if needed
  return { session, profile }
}

/**
 * Guest loader — ensures logged-in users are redirected away from auth pages.
 * Use this on /auth/login and /auth/register.
 */
export async function guestLoader() {
  const { data: { session } } = await supabase.auth.getSession()

  // Already logged in → redirect to dashboard
  if (session) {
    throw redirect('/')
  }

  return null
}
